import { useState, useCallback } from 'react'
import { Box, Flex, Heading, Button, Input, NativeSelect, Switch, Text, HStack } from '@chakra-ui/react'
import { Plus, Search, Link2 } from 'lucide-react'
import { JournalView, FiscalYearSelector, TransactionCreateModal, LettrageModal } from '../../components'
import type { ViewMode } from '../../components/JournalView'
import type { TransactionModalMode } from '../../components/TransactionCreateModal'
import { useJournals } from '../../hooks'
import { useAccountingContext } from '../../context/AccountingContext'
import type { TransactionFilters, Transaction } from '../../types'

export function TransactionsTab() {
  const { residenceId, activeFiscalYear, setSelectedFiscalYear } = useAccountingContext()
  const fiscalYearId = activeFiscalYear?.id
  const { data: journals } = useJournals()
  const [viewMode, setViewMode] = useState<ViewMode>('detailed')

  // État pour la modal de création/édition/duplication
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [modalMode, setModalMode] = useState<TransactionModalMode>('create')
  const [transactionToEdit, setTransactionToEdit] = useState<Transaction | null>(null)

  // État pour la modal de lettrage
  const [isLettrageModalOpen, setIsLettrageModalOpen] = useState(false)

  const [filters, setFilters] = useState<TransactionFilters>({
    residence_id: residenceId,
    fiscal_year_id: fiscalYearId,
  })

  const handleFilterChange = (key: keyof TransactionFilters, value: string) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value || undefined,
    }))
  }

  // Handlers pour les actions
  const handleCreateNew = useCallback(() => {
    setModalMode('create')
    setTransactionToEdit(null)
    setIsModalOpen(true)
  }, [])

  const handleEdit = useCallback((transaction: Transaction) => {
    setModalMode('edit')
    setTransactionToEdit(transaction)
    setIsModalOpen(true)
  }, [])

  const handleDuplicate = useCallback((transaction: Transaction) => {
    setModalMode('duplicate')
    setTransactionToEdit(transaction)
    setIsModalOpen(true)
  }, [])

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false)
    setTransactionToEdit(null)
    setModalMode('create')
  }, [])

  return (
    <Box>
      {/* Header avec sélecteur d'exercice */}
      <Flex justify="flex-end" mb={4}>
        <FiscalYearSelector
          residenceId={residenceId}
          value={activeFiscalYear?.id}
          onChange={setSelectedFiscalYear}
        />
      </Flex>

      <Box bg="white" p={5} borderRadius="lg" boxShadow="sm">
        <Flex justify="space-between" align="center" mb={4}>
          <Heading size="md">Écritures comptables</Heading>
          <HStack gap={2}>
            <Button
              variant="outline"
              colorPalette="brand"
              size="sm"
              onClick={() => setIsLettrageModalOpen(true)}
            >
              <Link2 size={16} />
              Lettrage
            </Button>
            <Button
              colorPalette="brand"
              size="sm"
              onClick={handleCreateNew}
            >
              <Plus size={16} />
              Nouvelle écriture
            </Button>
          </HStack>
        </Flex>

        {/* Filtres et toggle vue */}
        <Flex gap={3} mb={4} align="center" justify="space-between">
          <Flex gap={3} align="center">
            <Box position="relative" flex="none">
              <Box
                position="absolute"
                left={3}
                top="50%"
                transform="translateY(-50%)"
                pointerEvents="none"
                color="gray.400"
              >
                <Search size={16} />
              </Box>
              <Input
                pl={10}
                w="180px"
                placeholder="Rechercher..."
                value={filters.search || ''}
                onChange={(e) => handleFilterChange('search', e.target.value)}
              />
            </Box>

            <NativeSelect.Root flex="none" w="auto">
              <NativeSelect.Field
                value={filters.journal_id || ''}
                onChange={(e) => handleFilterChange('journal_id', e.target.value)}
              >
                <option value="">Tous les journaux</option>
                {journals?.map((j) => (
                  <option key={j.id} value={j.id}>
                    {j.code} - {j.name}
                  </option>
                ))}
              </NativeSelect.Field>
              <NativeSelect.Indicator />
            </NativeSelect.Root>

            <NativeSelect.Root flex="none" w="auto">
              <NativeSelect.Field
                value={filters.status || ''}
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <option value="">Tous les statuts</option>
                <option value="draft">Brouillon</option>
                <option value="validated">Validé</option>
                <option value="reversed">Contrepassé</option>
              </NativeSelect.Field>
              <NativeSelect.Indicator />
            </NativeSelect.Root>

            <Input
              type="date"
              flex="none"
              w="auto"
              value={filters.date_from || ''}
              onChange={(e) => handleFilterChange('date_from', e.target.value)}
            />

            <Input
              type="date"
              flex="none"
              w="auto"
              value={filters.date_to || ''}
              onChange={(e) => handleFilterChange('date_to', e.target.value)}
            />
          </Flex>

          <HStack
            gap={2}
            cursor="pointer"
            onClick={() => setViewMode(viewMode === 'detailed' ? 'synthetic' : 'detailed')}
          >
            <Text fontSize="sm" color={viewMode === 'synthetic' ? 'brand.600' : 'gray.500'} fontWeight={viewMode === 'synthetic' ? 'medium' : 'normal'}>
              Synthétique
            </Text>
            <Switch.Root
              checked={viewMode === 'detailed'}
              colorPalette="brand"
              size="sm"
            >
              <Switch.Control>
                <Switch.Thumb />
              </Switch.Control>
            </Switch.Root>
            <Text fontSize="sm" color={viewMode === 'detailed' ? 'brand.600' : 'gray.500'} fontWeight={viewMode === 'detailed' ? 'medium' : 'normal'}>
              Détaillé
            </Text>
          </HStack>
        </Flex>

        <JournalView
          viewMode={viewMode}
          filters={{
            ...filters,
            residence_id: residenceId,
            fiscal_year_id: fiscalYearId,
          }}
          onEdit={handleEdit}
          onDuplicate={handleDuplicate}
        />
      </Box>

      {/* Modal de création/édition/duplication */}
      <TransactionCreateModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        residenceId={residenceId}
        fiscalYearId={fiscalYearId}
        modalMode={modalMode}
        transactionToEdit={transactionToEdit}
      />

      {/* Modal de lettrage */}
      <LettrageModal
        isOpen={isLettrageModalOpen}
        onClose={() => setIsLettrageModalOpen(false)}
      />
    </Box>
  )
}
